/**
 * GameRenderer - WebGL Renderer für Boomfection
 * PRODUCTION RENDERER — ThreeRenderer.js is EXPERIMENTAL only, do NOT replace.
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 * @version 2026-01-19-v5 (Sharp black border via shader)
 */

import { VIRUS_TYPE_IDS } from './shared/VirusTypes.js';
// Version 2026-04-05-v14 — Pillen-Icons, Virus-Evolution, typ-spezifische Explosionen

// Pillen-Typ → Shader-Index Lookup (Zero-Allocation, frozen)
const PILL_TYPE_INDEX = Object.freeze({
    neutralizer: 1, mutation1: 2, mutation2: 3, mutation3: 4, swallow: 5,
    boost: 6, freeze: 7, sniper: 8, shuffle: 9, magnet: 10,
    poison: 11, mystery: 12, shield: 13, leech: 14, swap: 15
});

// Virus-Typ → Shader-Index (1..n) — aus der geteilten kanonischen Liste abgeleitet,
// damit Renderer und Server nie auseinanderdriften (0 = neutral/kein Besitzer)
const VIRUS_TYPE_INDEX = Object.freeze(Object.fromEntries(VIRUS_TYPE_IDS.map((id, i) => [id, i + 1])));

// ============================================================================
// RENDERING CONSTANTS (Mirror of src/game/config/GameConstants.js)
// ============================================================================

const RENDERING = {
    /** Render-Radius der Sphäre in Pixeln */
    SPHERE_RADIUS: 120,

    /** Standard Auto-Rotation Speed (0 = deaktiviert) */
    AUTO_ROTATE_SPEED: 0,

    /** Standard Zoom-Level */
    DEFAULT_ZOOM: 300,

    /** Minimaler Zoom */
    MIN_ZOOM: 100,

    /** Maximaler Zoom */
    MAX_ZOOM: 600
};

export class GameRenderer {
    constructor(canvas) {
        this.canvas = canvas;
        this.gl = canvas.getContext('webgl', { antialias: true, alpha: false });

        if (!this.gl) {
            throw new Error('WebGL nicht verfügbar');
        }

        this.game = null;
        this.rotation = this.quatCreate();
        this.zoom = RENDERING.DEFAULT_ZOOM;
        // Auto-Rotation deaktiviert - nur manuelle Rotation durch Benutzer
        this.autoRotateSpeed = RENDERING.AUTO_ROTATE_SPEED;
        this.time = 0;

        // Interaktion
        this.isDragging = false;
        this.lastMouse = { x: 0, y: 0 };
        this.hoveredFieldId = null;
        this.selectedFieldId = null;

        // Spieler-Kamerapositionen (rotation + zoom pro Spieler)
        this.playerCameraStates = new Map();

        // Callbacks
        this.onCameraChange = null; // Wird bei Kamera-Bewegung aufgerufen

        // Animation für sanfte Kamera-Rotation und Zoom
        this.targetRotation = null;
        this.targetZoom = null;
        this.animationSpeed = 0.15;

        // centerOnField: iteratives Zentrieren eines Feldes
        this.centerFieldId = null; // Wenn gesetzt, wird dieses Feld jeden Frame Richtung Mitte gesteuert
        this.centerSpeed = 0.08; // Wie schnell pro Frame (0-1, 0.08 = sanft)

        // Chain-follow: Kamera folgt Kettenreaktionen
        this._chainFollowActive = false;
        this._chainFollowTimer = null;

        // Explosions-Felder (für Shader-Effekt)
        this.explodingFields = new Map(); // fieldId -> { startTime, duration }

        // Buffer
        this.buffers = {};
        this.programs = {};

        // Callbacks
        this.onFieldClick = null;
        this.onFieldHover = null;

        // WebGL Context Loss State
        this.contextLost = false;

        this.init();
    }

    init() {
        this.setupContextLossHandlers();
        this.createShaders();
        this.createBuffers();
        this.setupEvents();
        this._resizeListener = () => this.resize();
        this.resize();
        window.addEventListener('resize', this._resizeListener);
    }

    /**
     * Cleanup: Event Listener entfernen
     */
    destroy() {
        window.removeEventListener('resize', this._resizeListener);
        if (this._trackedListeners) {
            for (const [target, type, fn, opts] of this._trackedListeners) {
                target.removeEventListener(type, fn, opts);
            }
            this._trackedListeners = [];
        }
    }

    /**
     * WebGL Context Loss/Restore Handler einrichten
     * Verhindert Crashes bei GPU-Reset oder Tab-Wechsel auf mobilen Geräten
     */
    setupContextLossHandlers() {
        this.canvas.addEventListener(
            'webglcontextlost',
            (event) => {
                event.preventDefault();
                this.contextLost = true;
                console.warn('[GameRenderer] WebGL Context Lost - Waiting for restore...');
            },
            false
        );

        this.canvas.addEventListener(
            'webglcontextrestored',
            () => {
                console.info('[GameRenderer] WebGL Context Restored - Reinitializing...');
                this.contextLost = false;
                this.reinitializeWebGL();
            },
            false
        );
    }

    /**
     * WebGL nach Context Loss neu initialisieren
     */
    reinitializeWebGL() {
        try {
            // Shader und Buffers neu erstellen
            this.createShaders();
            this.createBuffers();

            // Geometrie neu aufbauen falls Spiel läuft
            if (this.game) {
                this.buildGeometry();
            }

            console.info('[GameRenderer] WebGL successfully reinitialized');
        } catch (error) {
            console.error('[GameRenderer] Failed to reinitialize WebGL:', error);
        }
    }

    createShaders() {
        const gl = this.gl;

        // Main shader - einfach und performant
        const mainVS = `
            attribute vec3 aPosition;
            attribute vec3 aColor;
            attribute float aSelected;
            attribute float aHovered;
            attribute float aClickable;
            attribute float aCritical;
            attribute float aFillLevel;
            attribute float aAIPulse;
            attribute float aAITarget;
            attribute float aEdgeDist;
            attribute float aPillType;
            attribute vec2 aFaceUV;
            attribute float aVirusType;

            uniform mat4 uModelViewMatrix;
            uniform mat4 uProjectionMatrix;

            varying vec3 vColor;
            varying float vSelected;
            varying float vHovered;
            varying float vClickable;
            varying float vCritical;
            varying float vFillLevel;
            varying float vAIPulse;
            varying float vAITarget;
            varying float vEdgeDist;
            varying float vPillType;
            varying vec2 vFaceUV;
            varying float vVirusType;
            varying vec3 vPos;

            void main() {
                vColor = aColor;
                vSelected = aSelected;
                vHovered = aHovered;
                vClickable = aClickable;
                vCritical = aCritical;
                vFillLevel = aFillLevel;
                vAIPulse = aAIPulse;
                vAITarget = aAITarget;
                vEdgeDist = aEdgeDist;
                vPillType = aPillType;
                vFaceUV = aFaceUV;
                vVirusType = aVirusType;
                vPos = aPosition;
                gl_Position = uProjectionMatrix * uModelViewMatrix * vec4(aPosition, 1.0);
            }
        `;

        const mainFS = `
            // Hash-Noise bandet auf mediump (10-Bit-Mantisse): highp wo verfuegbar,
            // mediump als portabler Fallback (kein Kompilierrisiko).
            #ifdef GL_FRAGMENT_PRECISION_HIGH
            precision highp float;
            #else
            precision mediump float;
            #endif

            varying vec3 vColor;
            varying float vSelected;
            varying float vHovered;
            varying float vClickable;
            varying float vCritical;
            varying float vFillLevel;
            varying float vAIPulse;
            varying float vAITarget;
            varying float vEdgeDist;
            varying float vPillType;
            varying vec2 vFaceUV;
            varying float vVirusType;
            varying vec3 vPos;

            uniform float uTime;

            // -- Virus-Texturen (prozedural; Domaene = Kugelrichtung, haftet an der Kugel) --
            float vHash3(vec3 p) {
                return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
            }
            float vNoise3(vec3 p) {
                vec3 i = floor(p);
                vec3 f = fract(p);
                f = f * f * (3.0 - 2.0 * f);
                float n000 = vHash3(i);
                float n100 = vHash3(i + vec3(1.0, 0.0, 0.0));
                float n010 = vHash3(i + vec3(0.0, 1.0, 0.0));
                float n110 = vHash3(i + vec3(1.0, 1.0, 0.0));
                float n001 = vHash3(i + vec3(0.0, 0.0, 1.0));
                float n101 = vHash3(i + vec3(1.0, 0.0, 1.0));
                float n011 = vHash3(i + vec3(0.0, 1.0, 1.0));
                float n111 = vHash3(i + vec3(1.0, 1.0, 1.0));
                float nx00 = mix(n000, n100, f.x);
                float nx10 = mix(n010, n110, f.x);
                float nx01 = mix(n001, n101, f.x);
                float nx11 = mix(n011, n111, f.x);
                return mix(mix(nx00, nx10, f.y), mix(nx01, nx11, f.y), f.z);
            }
            float vFbm3(vec3 p) {
                return 0.6 * vNoise3(p) + 0.4 * vNoise3(p * 2.3 + 17.0);
            }

            // -- Pillen-Kapsel ----------------------------------------------
            // Körper = Kategorie-Farbe (defensiv/offensiv/chaos/special),
            // Kappe = Typ-Akzent. Indizes wie PILL_TYPE_INDEX (1..15).
            bool pillIsDefensive(float t) { return t < 1.5 || (t > 6.5 && t < 7.5) || (t > 12.5 && t < 13.5); }
            bool pillIsOffensive(float t) { return (t > 5.5 && t < 6.5) || (t > 9.5 && t < 11.5) || (t > 13.5 && t < 14.5); }
            bool pillIsSpecial(float t)   { return (t > 4.5 && t < 5.5) || (t > 7.5 && t < 8.5) || (t > 11.5 && t < 12.5); }

            vec3 pillBodyColor(float t) {
                if (pillIsDefensive(t)) return vec3(0.28, 0.56, 0.86); // kühles Stahlblau
                if (pillIsOffensive(t)) return vec3(0.86, 0.30, 0.22); // warmes Rot-Orange
                if (pillIsSpecial(t))   return vec3(0.92, 0.72, 0.26); // Gold/Amber
                return vec3(0.62, 0.36, 0.86);                         // Chaos: Violett
            }

            vec3 pillCapColor(float t) {
                if (t < 1.5)  return vec3(0.82, 0.84, 0.88); // 1  neutralizer
                if (t < 2.5)  return vec3(0.45, 0.95, 0.50); // 2  mutation1
                if (t < 3.5)  return vec3(0.75, 0.45, 1.00); // 3  mutation2
                if (t < 4.5)  return vec3(1.00, 0.50, 0.78); // 4  mutation3
                if (t < 5.5)  return vec3(0.12, 0.12, 0.16); // 5  swallow
                if (t < 6.5)  return vec3(1.00, 0.90, 0.35); // 6  boost
                if (t < 7.5)  return vec3(0.62, 0.92, 1.00); // 7  freeze
                if (t < 8.5)  return vec3(1.00, 0.62, 0.20); // 8  sniper
                if (t < 9.5)  return vec3(1.00, 0.35, 0.95); // 9  shuffle
                if (t < 10.5) return vec3(1.00, 0.45, 0.45); // 10 magnet
                if (t < 11.5) return vec3(0.55, 0.95, 0.35); // 11 poison
                if (t < 12.5) return vec3(1.00, 0.78, 1.00); // 12 mystery
                if (t < 13.5) return vec3(0.58, 0.72, 1.00); // 13 shield
                if (t < 14.5) return vec3(0.62, 0.14, 0.20); // 14 leech
                return vec3(1.00, 0.60, 0.25);               // 15 swap
            }

            // Atem-Tempo nach Kategorie: offensiv schnell, defensiv ruhig, Rest mittel
            float pillBreathSpeed(float t) {
                if (pillIsOffensive(t)) return 4.5;
                if (pillIsDefensive(t)) return 1.8;
                return 3.0;
            }

            // Signed Distance einer Kapsel (Stadion) um den Ursprung: Halbachse L, Radius R
            float sdCapsule(vec2 p, float L, float R) {
                p.x -= clamp(p.x, -L, L);
                return length(p) - R;
            }

            void main() {
                vec3 color = vColor;

                // Helligkeit basierend auf Füllstand
                float brightness = 1.0 - vFillLevel * 0.7;

                // Kritisch: am dunkelsten + Pulsieren
                if (vCritical > 0.9) {
                    float pulse = sin(uTime * 5.0) * 0.5 + 0.5;
                    brightness = 0.2 + pulse * 0.15;
                }

                color = color * brightness;

                // Virus-Textur: Blob = lebender Schleim (nur Felder mit Blob-Besitzer).
                // Noise-Domaene ist die Kugelrichtung: die Textur haftet an der Kugel und
                // laeuft ueber benachbarte eigene Felder zu EINEM Schleimkoerper zusammen.
                if (vVirusType > 2.5 && vVirusType < 3.5) {
                    vec3 dir = normalize(vPos);
                    vec3 wobble = 0.12 * sin(uTime * 0.7 + dir.yzx * 6.0);
                    vec3 q = (dir + wobble) * 28.0;
                    float n = vFbm3(q);
                    // Schleimblasen: hellere, leicht gruen-gelbliche Woelbungen, mehr bei vollem Feld
                    float bubbleAmt = 0.45 + 0.55 * vFillLevel;
                    float bubbles = smoothstep(0.48, 0.72, n) * bubbleAmt;
                    vec3 slime = color * 1.3 + vec3(0.06, 0.14, 0.0);
                    color = mix(color, slime, bubbles * 0.65);
                    // Dunkle Adern zwischen den Blasen
                    float veins = 1.0 - smoothstep(0.22, 0.36, n);
                    color *= 1.0 - veins * 0.28;
                    // Nasser Glanz: feine, langsam wandernde Highlights
                    // Begrenzte, langsam schwingende Verschiebung statt linearer uTime-Drift:
                    // unbegrenzt wachsende Hash-Argumente degradieren sin() in float32 nach Stunden.
                    float g = vNoise3(q * 2.6 + vec3(0.0, 2.0 * sin(uTime * 0.11), 0.0));
                    float gloss = pow(smoothstep(0.62, 0.92, g), 2.0);
                    color = mix(color, vec3(0.95, 1.0, 0.9), gloss * 0.38);
                }

                // KI-Denk-Puls
                if (vAIPulse > 0.5 && vAITarget < 0.5) {
                    float pulse = sin(uTime * 2.5) * 0.3 + 0.7;
                    vec3 glowColor = vColor * 1.5;
                    glowColor = mix(glowColor, vec3(1.0), 0.2);
                    color = mix(color, glowColor, pulse * 0.6);
                }

                // KI-Zielfeld
                if (vAITarget > 0.5) {
                    float pulse = sin(uTime * 4.0) * 0.5 + 0.5;
                    vec3 targetColor = vColor * 2.0;
                    targetColor = mix(targetColor, vec3(1.0), 0.3);
                    float glowIntensity = 0.7 + pulse * 0.3;
                    color = targetColor * glowIntensity;
                    float flash = pow(pulse, 3.0) * 0.4;
                    color = mix(color, vec3(1.0), flash);
                }

                // Klickbare Felder
                if (vClickable > 0.5 && vSelected < 0.5 && vHovered < 0.5) {
                    float pulse = sin(uTime * 2.0) * 0.1 + 0.9;
                    color = color * pulse;
                }

                // Hover-Effekt
                if (vHovered > 0.5) {
                    float pulse = sin(uTime * 2.5) * 0.3 + 0.7;
                    float outline = smoothstep(0.88, 0.98, vEdgeDist);
                    color = mix(color, vec3(1.0), outline * pulse);
                }

                // Auswahl-Effekt
                if (vSelected > 0.5) {
                    float pulse = sin(uTime * 4.0) * 0.5 + 0.5;
                    float edgeGlow = smoothstep(0.4, 0.9, vEdgeDist);
                    vec3 glowColor = vec3(1.0, 1.0, 0.8);
                    float glowIntensity = edgeGlow * (0.7 + pulse * 0.3);
                    color = mix(color, glowColor, glowIntensity);
                }

                // Pillen-Kapsel: vPillType ist konstant über die Fläche, die Form
                // kommt ausschliesslich aus der lokalen Flaechen-UV (Zentrum 0,0 / Rand ~ 1).
                if (vPillType > 0.5) {
                    vec2 p = vFaceUV;
                    float breathe = 0.5 + 0.5 * sin(uTime * pillBreathSpeed(vPillType));
                    float L = 0.21;
                    float R = 0.155 * (1.0 + 0.035 * breathe);
                    float d = sdCapsule(p, L, R);

                    vec3 body = pillBodyColor(vPillType);
                    vec3 cap = pillCapColor(vPillType);

                    // Weicher Schein auf dem Feld in Kategorie-Farbe, atmet leicht
                    float halo = (1.0 - smoothstep(0.0, 0.22, d)) * (0.18 + 0.14 * breathe);
                    color = mix(color, body, halo);

                    // Zweifarbig: links Körper, rechts Kappe, weiche Naht
                    vec3 base = mix(body, cap, smoothstep(-0.025, 0.025, p.x));

                    // Rundung quer zur Kapsel: hell am Grat, dunkel zum Rand (3D-Walze)
                    float h = clamp(1.0 - abs(p.y) / R, 0.0, 1.0);
                    base *= 0.52 + 0.48 * sqrt(h);

                    // Glanzstreifen leicht oberhalb der Mitte
                    float gloss = 1.0 - smoothstep(0.0, 0.045, abs(p.y - R * 0.38));
                    base = mix(base, vec3(1.0), gloss * 0.35);

                    // Rim-Light an der Kontur
                    float rim = 1.0 - smoothstep(0.0, 0.03, -d);
                    base = mix(base, vec3(1.0), rim * 0.22);

                    // Antialiasing-Kante
                    float a = 1.0 - smoothstep(-0.02, 0.02, d);
                    color = mix(color, base, a);
                }

                // Schwarzer Border mit Antialiasing
                float borderFactor = smoothstep(0.92, 0.95, vEdgeDist);
                color = mix(color, vec3(0.0, 0.0, 0.0), borderFactor);

                gl_FragColor = vec4(color, 1.0);
            }
        `;

        // Picking shader
        const pickVS = `
            attribute vec3 aPosition;
            attribute float aFieldId;

            uniform mat4 uModelViewMatrix;
            uniform mat4 uProjectionMatrix;

            varying float vFieldId;

            void main() {
                vFieldId = aFieldId;
                gl_Position = uProjectionMatrix * uModelViewMatrix * vec4(aPosition, 1.0);
            }
        `;

        const pickFS = `
            precision mediump float;
            varying float vFieldId;

            void main() {
                float id = vFieldId;
                float r = mod(id, 256.0) / 255.0;
                float g = mod(floor(id / 256.0), 256.0) / 255.0;
                float b = floor(id / 65536.0) / 255.0;
                gl_FragColor = vec4(r, g, b, 1.0);
            }
        `;

        this.programs.main = this.createProgram(mainVS, mainFS);
        this.programs.picking = this.createProgram(pickVS, pickFS);

        // Uniform-/Attribut-Locations einmalig cachen (konstant nach Link).
        // Wird bei jedem createShaders()-Aufruf (auch nach Context-Restore) neu aufgebaut.
        this.cacheProgramLocations();

        // Picking framebuffer
        this.pickingTexture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this.pickingTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

        this.pickingFramebuffer = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.pickingFramebuffer);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.pickingTexture, 0);

        this.pickingRenderbuffer = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, this.pickingRenderbuffer);
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, 1, 1);
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, this.pickingRenderbuffer);

        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    createProgram(vsSource, fsSource) {
        const gl = this.gl;

        const vs = gl.createShader(gl.VERTEX_SHADER);
        gl.shaderSource(vs, vsSource);
        gl.compileShader(vs);

        // Vertex Shader Kompilierung prüfen
        if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) {
            const error = gl.getShaderInfoLog(vs);
            gl.deleteShader(vs);
            console.error('[GameRenderer] Vertex Shader Compilation Error:', error);
            throw new Error(`Vertex Shader Compilation Error: ${error}`);
        }

        const fs = gl.createShader(gl.FRAGMENT_SHADER);
        gl.shaderSource(fs, fsSource);
        gl.compileShader(fs);

        // Fragment Shader Kompilierung prüfen
        if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
            const error = gl.getShaderInfoLog(fs);
            gl.deleteShader(vs);
            gl.deleteShader(fs);
            console.error('[GameRenderer] Fragment Shader Compilation Error:', error);
            throw new Error(`Fragment Shader Compilation Error: ${error}`);
        }

        const program = gl.createProgram();
        gl.attachShader(program, vs);
        gl.attachShader(program, fs);
        gl.linkProgram(program);

        // Program Linking prüfen
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            const error = gl.getProgramInfoLog(program);
            gl.deleteProgram(program);
            gl.deleteShader(vs);
            gl.deleteShader(fs);
            console.error('[GameRenderer] Shader Program Link Error:', error);
            throw new Error(`Shader Program Link Error: ${error}`);
        }

        // Shader nach erfolgreichem Linking freigeben (Best Practice)
        gl.deleteShader(vs);
        gl.deleteShader(fs);

        return program;
    }

    /**
     * Uniform-/Attribut-Locations einmalig pro Programm cachen.
     *
     * WebGL-Locations sind nach dem Program-Link konstant, daher genügt ein
     * einmaliger Lookup statt String-basierter Abfragen pro Frame. getUniformLocation
     * kann null liefern (wegoptimiertes Uniform); getAttribLocation liefert -1 (nicht
     * gefunden) — beide Werte werden 1:1 gecacht, sodass das Renderverhalten identisch
     * bleibt (gl.uniform*(null, ...) ist ein No-op, loc < 0 wird in bindAttribute
     * übersprungen). Locations sind pro Programm getrennt, da gleiche Namen in main/
     * picking unterschiedliche Locations haben können. Wird bei Context-Restore über
     * createShaders() automatisch neu aufgebaut.
     */
    cacheProgramLocations() {
        const gl = this.gl;

        this._uniformLocs = {
            main: {
                modelView: gl.getUniformLocation(this.programs.main, 'uModelViewMatrix'),
                projection: gl.getUniformLocation(this.programs.main, 'uProjectionMatrix'),
                time: gl.getUniformLocation(this.programs.main, 'uTime'),
            },
            picking: {
                modelView: gl.getUniformLocation(this.programs.picking, 'uModelViewMatrix'),
                projection: gl.getUniformLocation(this.programs.picking, 'uProjectionMatrix'),
            },
        };

        // Attribut-Locations pro (Programm, Name) — Map keyed by Programm-Objekt.
        // Frische Map bei jedem Aufruf, damit nach Re-Link keine alten Einträge bleiben.
        this._attribLocs = new Map();
        const cacheAttribs = (program, names) => {
            const map = {};
            for (const name of names) {
                map[name] = gl.getAttribLocation(program, name);
            }
            this._attribLocs.set(program, map);
        };
        cacheAttribs(this.programs.main, [
            'aPosition',
            'aColor',
            'aSelected',
            'aHovered',
            'aClickable',
            'aCritical',
            'aFillLevel',
            'aAIPulse',
            'aAITarget',
            'aEdgeDist',
            'aPillType',
            'aFaceUV',
            'aVirusType',
        ]);
        cacheAttribs(this.programs.picking, ['aPosition', 'aFieldId']);
    }

    createBuffers() {
        const gl = this.gl;
        this.buffers = {
            position: gl.createBuffer(),
            color: gl.createBuffer(),
            fieldId: gl.createBuffer(),
            selected: gl.createBuffer(),
            hovered: gl.createBuffer(),
            clickable: gl.createBuffer(),
            critical: gl.createBuffer(),
            fillLevel: gl.createBuffer(),
            aiPulse: gl.createBuffer(),
            aiTarget: gl.createBuffer(),
            edgeDist: gl.createBuffer(),
            pillType: gl.createBuffer(),
            faceUV: gl.createBuffer(),
            virusType: gl.createBuffer()
        };
    }

    setGame(game) {
        this.game = game;
        this.buildGeometry();
    }

    buildGeometry() {
        if (!this.game) return;

        const positions = [];
        const colors = [];
        const fieldIds = [];
        const selected = [];
        const hovered = [];
        const clickable = [];
        const critical = [];
        const fillLevel = [];
        const aiPulse = [];
        const aiTarget = [];
        const edgeDist = [];
        const pillType = [];
        const faceUV = [];
        const virusType = [];

        const sphere = this.game.sphere;
        const radius = RENDERING.SPHERE_RADIUS;

        for (const field of sphere.fields.values()) {
            // Feld-Zentrum skalieren
            const center = field.center.map((c) => c * radius);

            // Vertices direkt aus Field nutzen (bereits berechnet)
            const faceVertices = field.vertices.map((v) => v.map((c) => c * radius));

            if (faceVertices.length < 3) continue;

            // Farbe basierend auf Besitzer
            const color = this.getFieldColor(field);

            // Kritisch-Level und Füllstand
            const critLevel = this.getFieldCriticalLevel(field);
            // maxValue direkt vom Feld verwenden (Pentagon=5, Hexagon=6)
            const maxValue = field.maxValue || field.neighbors?.length || 6;
            const fill = field.value ? Math.min(field.value / maxValue, 1) : 0;

            // Pillen-Typ (0 = keine Pille)
            const pill = field.pill ? this.getPillTypeIndex(field.pill.type) : 0;
            const vt = this.getFieldVirusTypeIndex(field);

            // Lokales 2D-Koordinatensystem der Fläche (Pillen-Kapsel im Shader):
            // Zentrum = (0,0), Randvertices ≈ Einheitskreis. Tangente zeigt stabil zum
            // ersten Vertex, Bitangente = Normale × Tangente (deterministisch pro Feld).
            const nrm = this._normalize3(center);
            const d0 = this._sub3(faceVertices[0], center);
            const tan = this._normalize3(this._sub3(d0, this._scale3(nrm, this._dot3(d0, nrm))));
            const bit = this._cross3(nrm, tan);
            const faceR = this._len3(d0) || 1;

            // Dreiecke für das Polygon (Fan vom Zentrum)
            const n = faceVertices.length;
            for (let i = 0; i < n; i++) {
                const v1 = faceVertices[i];
                const v2 = faceVertices[(i + 1) % n];
                const e1 = this._sub3(v1, center);
                const e2 = this._sub3(v2, center);

                positions.push(...center, ...v1, ...v2);
                faceUV.push(
                    0, 0,
                    this._dot3(e1, tan) / faceR, this._dot3(e1, bit) / faceR,
                    this._dot3(e2, tan) / faceR, this._dot3(e2, bit) / faceR
                );
                colors.push(...color, ...color, ...color);
                fieldIds.push(field.id, field.id, field.id);
                selected.push(0, 0, 0);
                hovered.push(0, 0, 0);
                clickable.push(0, 0, 0);
                critical.push(critLevel, critLevel, critLevel);
                // Edge Distance: 0 = Zentrum, 1 = Rand
                edgeDist.push(0, 1, 1);
                fillLevel.push(fill, fill, fill);
                aiPulse.push(0, 0, 0);
                aiTarget.push(0, 0, 0);
                // Pillen-Typ konstant über die Fläche (Form kommt aus aFaceUV im Shader;
                // keine Interpolation → Farbschwellen stimmen überall, kein Farb-Bleeding)
                pillType.push(pill, pill, pill);
                virusType.push(vt, vt, vt);
            }
        }

        this.vertexCount = positions.length / 3;
        this.uploadBuffers(
            positions,
            colors,
            fieldIds,
            selected,
            hovered,
            clickable,
            critical,
            fillLevel,
            aiPulse,
            aiTarget,
            edgeDist,
            pillType,
            faceUV,
            virusType
        );
    }

    getPillTypeIndex(type) {
        return PILL_TYPE_INDEX[type] || 0;
    }

    /** Shader-Index des Virus-Typs des Feldbesitzers (0 = neutral) */
    getFieldVirusTypeIndex(field) {
        if (!field.ownerId || !this.game?.players) return 0;
        const owner = this.game.players.get(field.ownerId);
        return owner ? VIRUS_TYPE_INDEX[owner.virusType] || 0 : 0;
    }

    // Kleine vec3-Helfer ([x,y,z]) für die Flächen-UV in buildGeometry
    _sub3(a, b) {
        return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
    }

    _scale3(a, s) {
        return [a[0] * s, a[1] * s, a[2] * s];
    }

    _dot3(a, b) {
        return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    }

    _cross3(a, b) {
        return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
    }

    _len3(a) {
        return Math.sqrt(this._dot3(a, a));
    }

    _normalize3(a) {
        const l = this._len3(a) || 1;
        return [a[0] / l, a[1] / l, a[2] / l];
    }

    calculateFaceVertices(field, sphere, radius) {
        // Nachbar-Zentren sammeln
        const neighborCenters = field.neighbors
            .map((nId) => {
                const neighbor = sphere.getField(nId);
                return neighbor ? neighbor.centerNormalized : null;
            })
            .filter((c) => c !== null);

        if (neighborCenters.length < 3) {
            return [];
        }

        // Um Feld-Zentrum sortieren
        const sorted = this.sortAroundPoint(field.centerNormalized, neighborCenters);

        // Mittelpunkte zwischen Feld und Nachbarn berechnen
        const vertices = sorted.map((nc) => {
            const mid = this.normalize([
                (field.centerNormalized[0] + nc[0]) / 2,
                (field.centerNormalized[1] + nc[1]) / 2,
                (field.centerNormalized[2] + nc[2]) / 2
            ]);

            // Inset für Lücken zwischen Feldern
            const inset = 0.92;
            return [
                field.centerNormalized[0] * radius + (mid[0] * radius - field.centerNormalized[0] * radius) * inset,
                field.centerNormalized[1] * radius + (mid[1] * radius - field.centerNormalized[1] * radius) * inset,
                field.centerNormalized[2] * radius + (mid[2] * radius - field.centerNormalized[2] * radius) * inset
            ];
        });

        return vertices;
    }

    sortAroundPoint(center, points) {
        const up = this.normalize(center);
        let ref = [0, 1, 0];
        if (Math.abs(up[0] * ref[0] + up[1] * ref[1] + up[2] * ref[2]) > 0.9) {
            ref = [1, 0, 0];
        }

        const right = this.normalize(this.cross(up, ref));
        const forward = this.cross(right, up);

        const withAngles = points.map((p) => {
            const toP = [p[0] - center[0], p[1] - center[1], p[2] - center[2]];
            const x = this.dot(toP, right);
            const y = this.dot(toP, forward);
            return { point: p, angle: Math.atan2(y, x) };
        });

        withAngles.sort((a, b) => a.angle - b.angle);
        return withAngles.map((w) => w.point);
    }

    getFieldColor(field) {
        if (field.isBlocked) {
            // Blockiert — sehr dunkel (fast unsichtbar)
            return [0.08, 0.08, 0.1];
        }

        if (field.ownerId === null) {
            // Neutral - grau
            return [0.3, 0.3, 0.35];
        }

        // Spielerfarbe (konstant, unabhängig vom Wert)
        const player = this.game.players.get(field.ownerId);
        if (player && player.color) {
            return player.color.rgb.map((c) => c / 255);
        }

        return [0.5, 0.5, 0.5];
    }

    /**
     * Berechnet ob ein Feld kurz vor der Explosion steht
     */
    getFieldCriticalLevel(field) {
        if (field.ownerId === null || !field.value) return 0;

        const maxValue = field.neighbors?.length || 6;
        const value = field.value;

        // Kritisch wenn value >= maxValue (explodiert beim nächsten Klick)
        if (value >= maxValue) return 1.0;

        // Fast kritisch wenn value == maxValue - 1
        if (value === maxValue - 1) return 0.5;

        return 0;
    }

    uploadBuffers(
        positions,
        colors,
        fieldIds,
        selected,
        hovered,
        clickable,
        critical = [],
        fillLevel = [],
        aiPulse = [],
        aiTarget = [],
        edgeDist = [],
        pillType = [],
        faceUV = [],
        virusType = []
    ) {
        const gl = this.gl;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

        // Lokale Flächen-UV (vec2) — geometriestatisch wie position
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.faceUV);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array(faceUV.length ? faceUV : new Array((positions.length / 3) * 2).fill(0)),
            gl.STATIC_DRAW
        );

        // Virus-Typ des Besitzers (aendert sich mit dem Besitz → dynamisch)
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.virusType);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array(virusType.length ? virusType : new Array(positions.length / 3).fill(0)),
            gl.DYNAMIC_DRAW
        );

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.color);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.DYNAMIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.fieldId);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(fieldIds), gl.STATIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.selected);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(selected), gl.DYNAMIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.hovered);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(hovered), gl.DYNAMIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.clickable);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(clickable), gl.DYNAMIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.critical);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array(critical.length ? critical : new Array(positions.length / 3).fill(0)),
            gl.DYNAMIC_DRAW
        );

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.fillLevel);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array(fillLevel.length ? fillLevel : new Array(positions.length / 3).fill(0)),
            gl.DYNAMIC_DRAW
        );

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.aiPulse);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array(aiPulse.length ? aiPulse : new Array(positions.length / 3).fill(0)),
            gl.DYNAMIC_DRAW
        );

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.aiTarget);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array(aiTarget.length ? aiTarget : new Array(positions.length / 3).fill(0)),
            gl.DYNAMIC_DRAW
        );

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.edgeDist);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array(edgeDist.length ? edgeDist : new Array(positions.length / 3).fill(0)),
            gl.STATIC_DRAW
        );

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.pillType);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array(pillType.length ? pillType : new Array(positions.length / 3).fill(0)),
            gl.DYNAMIC_DRAW
        );
    }

    updateField(_field) {
        this.updateColors();
        this.updatePills();
    }

    updateColors() {
        if (!this.game) return;

        const colors = [];
        const critical = [];
        const fillLevel = [];
        const virusType = [];

        for (const field of this.game.sphere.fields.values()) {
            // Use vertices.length to match buildGeometry (skip fields without vertices)
            const n = field.vertices?.length || 0;
            if (n < 3) continue;

            const color = this.getFieldColor(field);
            const critLevel = this.getFieldCriticalLevel(field);
            // maxValue direkt vom Feld verwenden (Pentagon=5, Hexagon=6)
            const maxValue = field.maxValue || field.neighbors?.length || 6;
            const fill = field.value ? Math.min(field.value / maxValue, 1) : 0;
            const vt = this.getFieldVirusTypeIndex(field);

            for (let i = 0; i < n; i++) {
                colors.push(...color, ...color, ...color);
                critical.push(critLevel, critLevel, critLevel);
                fillLevel.push(fill, fill, fill);
                virusType.push(vt, vt, vt);
            }
        }

        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.color);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.DYNAMIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.critical);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(critical), gl.DYNAMIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.fillLevel);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(fillLevel), gl.DYNAMIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.virusType);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(virusType), gl.DYNAMIC_DRAW);
    }

    /**
     * Aktualisiert den Pillen-Buffer wenn sich Pillen ändern
     */
    updatePills() {
        if (!this.game) return;

        const pillType = [];

        for (const field of this.game.sphere.fields.values()) {
            const n = field.vertices?.length || 0;
            if (n < 3) continue;

            const pill = field.pill ? this.getPillTypeIndex(field.pill.type) : 0;

            // Pro Dreieck: 3 Vertices (center, v1, v2)
            for (let i = 0; i < n; i++) {
                // Pillen-Typ konstant über die Fläche (siehe buildGeometry)
                pillType.push(pill, pill, pill);
            }
        }

        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.pillType);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(pillType), gl.DYNAMIC_DRAW);
    }

    updateClickableFields(playerId, isPlacementPhase = false) {
        if (!this.game) return;

        const clickableFields = new Set(
            this.game.sphere.getClickableFields(playerId, isPlacementPhase).map((f) => f.id)
        );

        const clickable = [];
        for (const field of this.game.sphere.fields.values()) {
            // Use vertices.length to match buildGeometry (skip fields without vertices)
            const n = field.vertices?.length || 0;
            if (n < 3) continue;

            const isClickable = clickableFields.has(field.id) ? 1 : 0;
            for (let i = 0; i < n; i++) {
                clickable.push(isClickable, isClickable, isClickable);
            }
        }

        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.clickable);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(clickable), gl.DYNAMIC_DRAW);
    }

    /**
     * Startet Puls-Effekt für alle Felder eines Spielers (KI denkt)
     * @param {string} playerId - Spieler-ID
     */
    startAIPulse(playerId) {
        if (!this.game) return;

        this.aiPulsePlayerId = playerId;

        const aiPulse = [];
        for (const field of this.game.sphere.fields.values()) {
            const n = field.vertices?.length || 0;
            if (n < 3) continue;

            const isPulsing = field.ownerId === playerId ? 1 : 0;
            for (let i = 0; i < n; i++) {
                aiPulse.push(isPulsing, isPulsing, isPulsing);
            }
        }

        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.aiPulse);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(aiPulse), gl.DYNAMIC_DRAW);
    }

    /**
     * Stoppt den KI-Puls-Effekt
     */
    stopAIPulse() {
        if (!this.game) return;

        this.aiPulsePlayerId = null;

        const aiPulse = [];
        for (const field of this.game.sphere.fields.values()) {
            const n = field.vertices?.length || 0;
            if (n < 3) continue;

            for (let i = 0; i < n; i++) {
                aiPulse.push(0, 0, 0);
            }
        }

        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.aiPulse);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(aiPulse), gl.DYNAMIC_DRAW);
    }

    /**
     * Findet das beste Feld eines Spielers und rotiert dorthin
     * @param {string} playerId - Spieler-ID
     * @returns {number|null} - Feld-ID oder null wenn keine Felder
     */
    rotateToPlayerField(playerId) {
        if (!this.game) return null;

        // Alle Felder des Spielers sammeln
        const playerFields = [];
        for (const field of this.game.sphere.fields.values()) {
            if (field.ownerId === playerId && field.value > 0) {
                playerFields.push(field);
            }
        }

        if (playerFields.length === 0) return null;

        // Das Feld mit dem höchsten Wert wählen (oder random bei Gleichstand)
        playerFields.sort((a, b) => {
            if (b.value !== a.value) return b.value - a.value;
            return Math.random() - 0.5;
        });

        const bestField = playerFields[0];
        this.rotateToField(bestField.id);
        return bestField.id;
    }

    /**
     * Markiert ein Feld als KI-Ziel (pulsierender Ring-Effekt)
     * @param {number} fieldId - Feld-ID
     */
    setAITarget(fieldId) {
        if (!this.game) return;

        this.aiTargetFieldId = fieldId;

        const aiTarget = [];
        for (const field of this.game.sphere.fields.values()) {
            const n = field.vertices?.length || 0;
            if (n < 3) continue;

            const isTarget = field.id === fieldId ? 1 : 0;
            for (let i = 0; i < n; i++) {
                aiTarget.push(isTarget, isTarget, isTarget);
            }
        }

        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.aiTarget);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(aiTarget), gl.DYNAMIC_DRAW);
    }

    /**
     * Entfernt die KI-Ziel-Markierung
     */
    clearAITarget() {
        if (!this.game) return;

        this.aiTargetFieldId = null;

        const aiTarget = [];
        for (const field of this.game.sphere.fields.values()) {
            const n = field.vertices?.length || 0;
            if (n < 3) continue;

            for (let i = 0; i < n; i++) {
                aiTarget.push(0, 0, 0);
            }
        }

        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.aiTarget);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(aiTarget), gl.DYNAMIC_DRAW);
    }

    setupEvents() {
        const canvas = this.canvas;
        this._trackedListeners = [];
        const track = (target, type, fn, opts) => {
            target.addEventListener(type, fn, opts);
            this._trackedListeners.push([target, type, fn, opts]);
        };

        // Kontextmenü deaktivieren
        track(canvas, 'contextmenu', (e) => {
            e.preventDefault();
        });

        // Rechtsklick für Rotation
        track(canvas, 'mousedown', (e) => {
            if (e.button === 2) {
                // Rechtsklick
                this.isDragging = true;
                this.lastMouse = { x: e.clientX, y: e.clientY };
                // Manuelle Rotation bricht automatische Animation ab
                this.targetRotation = null;
                this.centerFieldId = null;
            }
        });

        track(canvas, 'mousemove', (e) => {
            if (this.isDragging) {
                const dx = e.clientX - this.lastMouse.x;
                const dy = e.clientY - this.lastMouse.y;
                this.rotate(dx, dy);
                this.lastMouse = { x: e.clientX, y: e.clientY };
            } else {
                this.pickField(e.clientX, e.clientY, false);
            }
        });

        track(canvas, 'mouseup', (e) => {
            if (e.button === 2) {
                // Rechtsklick
                this.isDragging = false;
            }
        });

        track(canvas, 'mouseleave', () => {
            this.isDragging = false;
            this.setHoveredField(null);
        });

        // Linksklick für Feld-Auswahl
        track(canvas, 'click', (e) => {
            if (e.button === 0 && !this.isDragging) {
                // Linksklick
                this.pickField(e.clientX, e.clientY, true);
            }
        });

        track(canvas, 'wheel',
            (e) => {
                e.preventDefault();
                this.zoom = Math.max(RENDERING.MIN_ZOOM, Math.min(RENDERING.MAX_ZOOM, this.zoom + e.deltaY * 0.3));
                // Callback für Kamera-Sync
                if (this.onCameraChange) {
                    this.onCameraChange();
                }
            },
            { passive: false }
        );

        // Touch state for pinch-to-zoom and double-tap
        this._pinchStartDist = 0;
        this._pinchStartZoom = 0;
        this._isPinching = false;
        this._lastTapTime = 0;
        this._lastTapX = 0;
        this._lastTapY = 0;
        this._touchStartPos = null;
        this._doubleTapZoomedIn = false;

        // Touch events
        track(canvas, 'touchstart',
            (e) => {
                if (e.touches.length === 2) {
                    // Pinch-to-zoom start
                    this._pinchStartDist = this._getTouchDistance(e.touches);
                    this._pinchStartZoom = this.zoom;
                    this._isPinching = true;
                    this.isDragging = false;
                    this._touchMoved = true; // Don't treat as tap
                    e.preventDefault();
                    return;
                }
                e.preventDefault();
                if (e.touches.length === 1) {
                    this.isDragging = true;
                    this._touchMoved = false; // Track if finger actually moves
                    this._touchStartTime = Date.now();
                    this._touchStartPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                    this.lastMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                    // Manuelle Rotation bricht ALLE automatischen Animationen ab
                    this.targetRotation = null;
                    this.centerFieldId = null;
                    this.stopChainFollow();
                }
            },
            { passive: false }
        );

        track(canvas, 'touchmove',
            (e) => {
                e.preventDefault();
                if (e.touches.length === 2 && this._isPinching) {
                    // Pinch-to-zoom move
                    const dist = this._getTouchDistance(e.touches);
                    const scale = dist / this._pinchStartDist;
                    this.zoom = Math.max(RENDERING.MIN_ZOOM, Math.min(RENDERING.MAX_ZOOM, this._pinchStartZoom / scale));
                    if (this.onCameraChange) this.onCameraChange();
                    return;
                }
                if (e.touches.length === 1 && this.isDragging && !this._isPinching) {
                    const dx = e.touches[0].clientX - this.lastMouse.x;
                    const dy = e.touches[0].clientY - this.lastMouse.y;
                    // Mark as moved if finger travels more than 5px total
                    if (!this._touchMoved && this._touchStartPos) {
                        const totalDx = e.touches[0].clientX - this._touchStartPos.x;
                        const totalDy = e.touches[0].clientY - this._touchStartPos.y;
                        if (totalDx * totalDx + totalDy * totalDy > 25) {
                            this._touchMoved = true;
                        }
                    }
                    this.rotate(dx, dy);
                    this.lastMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                }
            },
            { passive: false }
        );

        track(canvas, 'touchend', (e) => {
            if (e.touches.length === 0) {
                const endX = e.changedTouches[0]?.clientX || 0;
                const endY = e.changedTouches[0]?.clientY || 0;

                // Only process tap if finger didn't move (not a drag)
                if (this._touchStartPos && !this._isPinching && !this._touchMoved) {
                    const now = Date.now();
                    const tapDx = endX - (this._lastTapX || 0);
                    const tapDy = endY - (this._lastTapY || 0);
                    const tapDist = Math.sqrt(tapDx * tapDx + tapDy * tapDy);

                    if (now - (this._lastTapTime || 0) < 300 && tapDist < 30) {
                        // Double-tap: toggle zoom
                        if (this._doubleTapZoomedIn) {
                            this.targetZoom = RENDERING.DEFAULT_ZOOM;
                            this._doubleTapZoomedIn = false;
                        } else {
                            this.targetZoom = Math.max(RENDERING.MIN_ZOOM, this.zoom * 0.6);
                            this._doubleTapZoomedIn = true;
                        }
                        this._lastTapTime = 0;
                    } else {
                        // Single tap → select field
                        this.pickField(endX, endY, true);
                        this._lastTapTime = now;
                        this._lastTapX = endX;
                        this._lastTapY = endY;
                    }
                }

                this.isDragging = false;
                this._isPinching = false;
                this._touchStartPos = null;
                this._touchMoved = false;
            } else if (e.touches.length === 1) {
                // Transitioned from pinch back to single finger
                this._isPinching = false;
                this.isDragging = true;
                this.lastMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        });

        // Tastatur-Events für Rotation und Zoom
        track(document, 'keydown', (e) => {
            // Nur wenn kein Input-Feld fokussiert ist
            if (
                document.activeElement.tagName === 'INPUT' ||
                document.activeElement.tagName === 'TEXTAREA' ||
                document.activeElement.tagName === 'SELECT'
            ) {
                return;
            }

            const rotateAmount = 15; // Grad pro Tastendruck
            const zoomAmount = 30;

            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    this.targetRotation = null;
                    this.centerFieldId = null; // Animation abbrechen
                    this.rotate(-rotateAmount, 0);
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    this.targetRotation = null;
                    this.rotate(rotateAmount, 0);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    this.targetRotation = null;
                    this.rotate(0, -rotateAmount);
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    this.targetRotation = null;
                    this.rotate(0, rotateAmount);
                    break;
                case '+':
                case '=': // Für Tastaturen ohne NumPad
                    e.preventDefault();
                    this.zoom = Math.min(RENDERING.MAX_ZOOM, this.zoom + zoomAmount);
                    if (this.onCameraChange) this.onCameraChange();
                    break;
                case '-':
                case '_':
                    e.preventDefault();
                    this.zoom = Math.max(RENDERING.MIN_ZOOM, this.zoom - zoomAmount);
                    if (this.onCameraChange) this.onCameraChange();
                    break;
            }
        });
    }

    rotate(dx, dy) {
        const rotY = this.quatFromAxisAngle([0, 1, 0], -dx * 0.005);
        const rotX = this.quatFromAxisAngle([1, 0, 0], -dy * 0.005);
        this.rotation = this.quatNormalize(this.quatMultiply(rotY, this.quatMultiply(rotX, this.rotation)));

        // Callback für Kamera-Sync
        if (this.onCameraChange) {
            this.onCameraChange();
        }
    }

    /**
     * Zentriert ein Feld iterativ in der Mitte des Bildschirms.
     * Wird jeden Frame in updateCameraAnimation() aufgerufen bis das Feld zentriert ist.
     * @param {number} fieldId
     */
    centerOnField(fieldId) {
        this.centerFieldId = fieldId;
        this.targetRotation = null; // Alte SLERP-Animation abbrechen
    }

    /**
     * Stoppt das iterative Zentrieren
     */
    stopCentering() {
        this.centerFieldId = null;
    }

    /**
     * Follow a chain of explosions by panning camera to each field.
     * Only activates for chains >= 5. Respects prefers-reduced-motion.
     * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
     * @param {number[]} fieldIds - array of field IDs being exploded
     * @param {number} delayBetween - ms between each pan
     */
    followChain(fieldIds, delayBetween = 300) {
        this.stopChainFollow(); // Clear any existing chain follow
        if (!fieldIds || fieldIds.length < 5) return;
        if (this.isDragging) return; // Don't hijack camera during manual rotation

        // Respect prefers-reduced-motion
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this._chainFollowActive = true;
        let index = 0;

        const panNext = () => {
            if (index >= fieldIds.length || !this._chainFollowActive) {
                this._chainFollowActive = false;
                return;
            }
            this.centerOnField(fieldIds[index]);
            index++;
            this._chainFollowTimer = setTimeout(panNext, delayBetween);
        };

        panNext();
    }

    /**
     * Stops an active chain follow sequence and clears timers.
     * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
     */
    stopChainFollow() {
        this._chainFollowActive = false;
        if (this._chainFollowTimer) {
            clearTimeout(this._chainFollowTimer);
            this._chainFollowTimer = null;
        }
    }

    /**
     * Interner Update fuer centerOnField — wird jeden Frame aufgerufen.
     * Berechnet wo das Feld auf dem Screen ist und macht kleine rotate()-Schritte.
     */
    _updateCentering() {
        if (this.centerFieldId === null || !this.game) return;
        if (this.isDragging) return; // Don't fight manual rotation

        const field = this.game.sphere.getField(this.centerFieldId);
        if (!field || !field.centerNormalized) {
            this.centerFieldId = null;
            return;
        }

        // Wo ist das Feld aktuell auf dem Screen?
        const c = field.centerNormalized;
        const m = this.quatToMatrix(this.rotation);
        const sx = c[0] * m[0] + c[1] * m[4] + c[2] * m[8];
        const sy = c[0] * m[1] + c[1] * m[5] + c[2] * m[9];
        const sz = c[0] * m[2] + c[1] * m[6] + c[2] * m[10];

        // Zentriert genug? (Toleranz)
        if (Math.abs(sx) < 0.02 && Math.abs(sy) < 0.02 && sz > 0) {
            this.centerFieldId = null; // Fertig
            return;
        }

        // Berechne kleine Drehung Richtung Zentrum
        // Gleiche Formel wie rotate(dx, dy), aber mit begrenzter Geschwindigkeit
        const angleH = Math.atan2(sx, Math.abs(sz) + 0.001);
        const angleV = Math.atan2(-sy, Math.abs(sz) + 0.001);

        // Begrenzen auf maximal centerSpeed pro Frame
        const step = this.centerSpeed;
        const dh = angleH * step;
        const dv = angleV * step;

        // Exakt gleiche Rotation wie rotate() — das funktioniert nachweislich
        const rotY = this.quatFromAxisAngle([0, 1, 0], dh);
        const rotX = this.quatFromAxisAngle([1, 0, 0], dv);
        this.rotation = this.quatNormalize(this.quatMultiply(rotY, this.quatMultiply(rotX, this.rotation)));
    }

    /**
     * Legacy-Wrapper: ruft centerOnField auf
     */
    rotateToField(fieldId) {
        this.centerOnField(fieldId);
    }

    /**
     * Speichert die aktuelle Kameraposition für einen Spieler
     */
    savePlayerCamera(playerId) {
        if (!playerId) return;

        this.playerCameraStates.set(playerId, {
            rotation: new Float32Array(this.rotation),
            zoom: this.zoom
        });
    }

    /**
     * Stellt die gespeicherte Kameraposition eines Spielers wieder her
     */
    restorePlayerCamera(playerId) {
        if (!playerId) return;

        const state = this.playerCameraStates.get(playerId);
        if (state) {
            // Kopie erstellen, um Referenz-Probleme zu vermeiden
            this.targetRotation = new Float32Array(state.rotation);
            this.targetZoom = state.zoom;
        }
    }

    /**
     * Gibt den aktuellen Kamera-Zustand zurück (für Sync)
     * @returns {{rotation: number[], zoom: number}}
     */
    getCameraState() {
        return {
            rotation: Array.from(this.rotation),
            zoom: this.zoom
        };
    }

    /**
     * Setzt den Kamera-Zustand von extern (für Sync)
     * @param {{rotation: number[], zoom: number}} state
     * @param {boolean} animate - Sanft animieren oder sofort setzen
     */
    setCameraState(state, animate = true) {
        if (!state || !state.rotation) return;

        if (animate) {
            this.targetRotation = new Float32Array(state.rotation);
            this.targetZoom = state.zoom;
        } else {
            this.rotation = new Float32Array(state.rotation);
            this.zoom = state.zoom;
            this.targetRotation = null;
            this.targetZoom = null;
        }
    }

    /**
     * Löst einen Explosions-Effekt für ein Feld aus
     */
    triggerExplosion(fieldId) {
        this.explodingFields.set(fieldId, {
            startTime: this.time,
            duration: 0.8 // Sekunden (länger für bessere Sichtbarkeit)
        });
    }

    /**
     * Prüft ob ein Feld aktuell sichtbar ist (zur Kamera zeigt)
     * @param {number} fieldId - Die ID des Feldes
     * @returns {boolean} true wenn das Feld sichtbar ist
     */
    isFieldVisible(fieldId) {
        if (!this.game) return false;

        const field = this.game.sphere.getField(fieldId);
        if (!field || !field.centerNormalized) return false;

        // Feld-Zentrum durch Rotation transformieren
        const center = field.centerNormalized;
        const rotMatrix = this.quatToMatrix(this.rotation);

        // Transformiere den Punkt (Matrix * Vector)
        const rotatedZ = center[0] * rotMatrix[2] + center[1] * rotMatrix[6] + center[2] * rotMatrix[10];

        // Feld ist sichtbar wenn z > threshold (zur Kamera zeigend)
        // 0.3 als Schwellwert = ca. 72° vom Zentrum
        return rotatedZ > 0.3;
    }

    /**
     * Aktualisiert die Kamera-Animation (wird in render() aufgerufen)
     */
    updateCameraAnimation() {
        // Manuelle Rotation hat Vorrang — alle Auto-Animationen pausieren
        if (this.isDragging) return;

        // Rotation animieren
        if (this.targetRotation) {
            this.rotation = this.quatSlerp(this.rotation, this.targetRotation, this.animationSpeed);

            // Prüfen ob Ziel erreicht
            const diff =
                Math.abs(this.rotation[0] - this.targetRotation[0]) +
                Math.abs(this.rotation[1] - this.targetRotation[1]) +
                Math.abs(this.rotation[2] - this.targetRotation[2]) +
                Math.abs(this.rotation[3] - this.targetRotation[3]);

            if (diff < 0.001) {
                this.rotation = new Float32Array(this.targetRotation);
                this.targetRotation = null;
            }
        }

        // Zoom animieren
        if (this.targetZoom !== null) {
            const zoomDiff = this.targetZoom - this.zoom;
            this.zoom += zoomDiff * this.animationSpeed;

            // Prüfen ob Ziel erreicht
            if (Math.abs(zoomDiff) < 1) {
                this.zoom = this.targetZoom;
                this.targetZoom = null;
            }
        }

        // Iteratives Feld-Zentrieren (centerOnField)
        this._updateCentering();
    }

    /**
     * Quaternion Slerp (spherical linear interpolation)
     */
    quatSlerp(a, b, t) {
        let dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3];

        // Kürzesten Pfad wählen
        const bSign = dot < 0 ? -1 : 1;
        if (dot < 0) dot = -dot;

        let scale0, scale1;
        if (dot > 0.9995) {
            // Lineare Interpolation für sehr nahe Quaternions
            scale0 = 1 - t;
            scale1 = t * bSign;
        } else {
            const theta = Math.acos(dot);
            const sinTheta = Math.sin(theta);
            scale0 = Math.sin((1 - t) * theta) / sinTheta;
            scale1 = (Math.sin(t * theta) / sinTheta) * bSign;
        }

        return this.quatNormalize(
            new Float32Array([
                scale0 * a[0] + scale1 * b[0],
                scale0 * a[1] + scale1 * b[1],
                scale0 * a[2] + scale1 * b[2],
                scale0 * a[3] + scale1 * b[3]
            ])
        );
    }

    pickField(mouseX, mouseY, isClick) {
        if (!this.game) return;

        const gl = this.gl;
        const rect = this.canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        const x = (mouseX - rect.left) * dpr;
        const y = this.canvas.height - (mouseY - rect.top) * dpr;

        // Render to picking framebuffer
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.pickingFramebuffer);
        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        gl.clearColor(1, 1, 1, 1);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        this.renderPicking();

        // Read pixel
        const pixel = new Uint8Array(4);
        gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);

        const fieldId = pixel[0] + pixel[1] * 256 + pixel[2] * 65536;

        if (pixel[0] === 255 && pixel[1] === 255 && pixel[2] === 255) {
            // Kein Feld getroffen
            if (!isClick) {
                this.setHoveredField(null);
            }
        } else {
            if (isClick) {
                if (this.onFieldClick) {
                    this.onFieldClick(fieldId);
                }
            } else {
                this.setHoveredField(fieldId);
            }
        }
    }

    setHoveredField(fieldId) {
        if (this.hoveredFieldId === fieldId) return;

        this.hoveredFieldId = fieldId;

        if (this.onFieldHover) {
            this.onFieldHover(fieldId);
        }

        this.updateHoverBuffer();
    }

    updateHoverBuffer() {
        if (!this.game) return;

        const hovered = [];
        for (const field of this.game.sphere.fields.values()) {
            // Use vertices.length to match buildGeometry (skip fields without vertices)
            const n = field.vertices?.length || 0;
            if (n < 3) continue;

            const isHovered = field.id === this.hoveredFieldId ? 1 : 0;
            for (let i = 0; i < n; i++) {
                hovered.push(isHovered, isHovered, isHovered);
            }
        }

        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.hovered);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(hovered), gl.DYNAMIC_DRAW);
    }

    resize() {
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = this.canvas.clientWidth * dpr;
        this.canvas.height = this.canvas.clientHeight * dpr;

        const gl = this.gl;
        gl.viewport(0, 0, this.canvas.width, this.canvas.height);

        // Resize picking buffers
        gl.bindTexture(gl.TEXTURE_2D, this.pickingTexture);
        gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.RGBA,
            this.canvas.width,
            this.canvas.height,
            0,
            gl.RGBA,
            gl.UNSIGNED_BYTE,
            null
        );
        gl.bindRenderbuffer(gl.RENDERBUFFER, this.pickingRenderbuffer);
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, this.canvas.width, this.canvas.height);
    }

    render() {
        // Bei Context Loss nicht rendern
        if (this.contextLost) {
            return;
        }

        this.time += 0.016;

        // Kamera-Animation aktualisieren
        this.updateCameraAnimation();

        // Abgelaufene Explosionen entfernen
        for (const [fieldId, explosion] of this.explodingFields) {
            if (this.time - explosion.startTime > explosion.duration) {
                this.explodingFields.delete(fieldId);
            }
        }

        // Auto-rotation
        if (!this.isDragging && this.autoRotateSpeed > 0) {
            const rotY = this.quatFromAxisAngle([0, 1, 0], this.autoRotateSpeed);
            this.rotation = this.quatNormalize(this.quatMultiply(rotY, this.rotation));
        }

        const gl = this.gl;
        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        gl.clearColor(0.04, 0.04, 0.06, 1.0);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.CULL_FACE);

        if (!this.game || this.vertexCount === 0) return;

        this.renderMain();
    }

    renderMain() {
        const gl = this.gl;
        const program = this.programs.main;
        gl.useProgram(program);

        const matrices = this.getMatrices();
        const locs = this._uniformLocs.main;
        gl.uniformMatrix4fv(locs.modelView, false, matrices.modelView);
        gl.uniformMatrix4fv(locs.projection, false, matrices.projection);
        gl.uniform1f(locs.time, this.time);

        this.bindAttribute(program, 'aPosition', this.buffers.position, 3);
        this.bindAttribute(program, 'aColor', this.buffers.color, 3);
        this.bindAttribute(program, 'aSelected', this.buffers.selected, 1);
        this.bindAttribute(program, 'aHovered', this.buffers.hovered, 1);
        this.bindAttribute(program, 'aClickable', this.buffers.clickable, 1);
        this.bindAttribute(program, 'aCritical', this.buffers.critical, 1);
        this.bindAttribute(program, 'aFillLevel', this.buffers.fillLevel, 1);
        this.bindAttribute(program, 'aAIPulse', this.buffers.aiPulse, 1);
        this.bindAttribute(program, 'aAITarget', this.buffers.aiTarget, 1);
        this.bindAttribute(program, 'aEdgeDist', this.buffers.edgeDist, 1);
        this.bindAttribute(program, 'aPillType', this.buffers.pillType, 1);
        this.bindAttribute(program, 'aFaceUV', this.buffers.faceUV, 2);
        this.bindAttribute(program, 'aVirusType', this.buffers.virusType, 1);

        gl.drawArrays(gl.TRIANGLES, 0, this.vertexCount);
    }

    renderPicking() {
        const gl = this.gl;
        const program = this.programs.picking;
        gl.useProgram(program);

        const matrices = this.getMatrices();
        const locs = this._uniformLocs.picking;
        gl.uniformMatrix4fv(locs.modelView, false, matrices.modelView);
        gl.uniformMatrix4fv(locs.projection, false, matrices.projection);

        this.bindAttribute(program, 'aPosition', this.buffers.position, 3);
        this.bindAttribute(program, 'aFieldId', this.buffers.fieldId, 1);

        gl.enable(gl.DEPTH_TEST);
        gl.drawArrays(gl.TRIANGLES, 0, this.vertexCount);
    }

    bindAttribute(program, name, buffer, size) {
        const gl = this.gl;
        // Gecachte Location nutzen (konstant nach Link); Fallback auf Live-Lookup,
        // falls (untypisch) ein nicht gecachter Name/Programm angefragt wird.
        const progLocs = this._attribLocs && this._attribLocs.get(program);
        const loc =
            progLocs && Object.prototype.hasOwnProperty.call(progLocs, name)
                ? progLocs[name]
                : gl.getAttribLocation(program, name);
        if (loc >= 0) {
            gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
            gl.enableVertexAttribArray(loc);
            gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
        }
    }

    getMatrices() {
        const aspect = this.canvas.width / this.canvas.height;
        const size = this.zoom * 0.5;
        const projection = this.ortho(-size * aspect, size * aspect, -size, size, 1, 2000);
        let modelView = this.lookAt([0, 0, 500], [0, 0, 0], [0, 1, 0]);
        modelView = this.mat4Multiply(modelView, this.quatToMatrix(this.rotation));
        return { projection, modelView };
    }

    // Matrix/Quaternion helpers
    quatCreate() {
        return new Float32Array([0, 0, 0, 1]);
    }

    quatFromAxisAngle(axis, angle) {
        const h = angle / 2,
            s = Math.sin(h);
        return new Float32Array([axis[0] * s, axis[1] * s, axis[2] * s, Math.cos(h)]);
    }

    quatRotateVector(q, v) {
        // v' = q * v * q^-1 (fuer Unit-Quaternion: q^-1 = conjugate)
        const qv = new Float32Array([v[0], v[1], v[2], 0]);
        const qConj = new Float32Array([-q[0], -q[1], -q[2], q[3]]);
        const tmp = this.quatMultiply(q, qv);
        const result = this.quatMultiply(tmp, qConj);
        return [result[0], result[1], result[2]];
    }

    quatMultiply(a, b) {
        return new Float32Array([
            a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
            a[3] * b[1] + a[1] * b[3] + a[2] * b[0] - a[0] * b[2],
            a[3] * b[2] + a[2] * b[3] + a[0] * b[1] - a[1] * b[0],
            a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2]
        ]);
    }

    quatNormalize(q) {
        const len = Math.sqrt(q[0] * q[0] + q[1] * q[1] + q[2] * q[2] + q[3] * q[3]);
        return new Float32Array([q[0] / len, q[1] / len, q[2] / len, q[3] / len]);
    }

    quatToMatrix(q) {
        const [x, y, z, w] = q;
        const x2 = x + x,
            y2 = y + y,
            z2 = z + z;
        const xx = x * x2,
            xy = x * y2,
            xz = x * z2;
        const yy = y * y2,
            yz = y * z2,
            zz = z * z2;
        const wx = w * x2,
            wy = w * y2,
            wz = w * z2;
        return new Float32Array([
            1 - (yy + zz),
            xy + wz,
            xz - wy,
            0,
            xy - wz,
            1 - (xx + zz),
            yz + wx,
            0,
            xz + wy,
            yz - wx,
            1 - (xx + yy),
            0,
            0,
            0,
            0,
            1
        ]);
    }

    normalize(v) {
        const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
        return [v[0] / len, v[1] / len, v[2] / len];
    }

    cross(a, b) {
        return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
    }

    dot(a, b) {
        return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    }

    ortho(l, r, b, t, n, f) {
        return new Float32Array([
            2 / (r - l),
            0,
            0,
            0,
            0,
            2 / (t - b),
            0,
            0,
            0,
            0,
            2 / (n - f),
            0,
            (l + r) / (l - r),
            (b + t) / (b - t),
            (n + f) / (n - f),
            1
        ]);
    }

    lookAt(eye, center, up) {
        const z = this.normalize([eye[0] - center[0], eye[1] - center[1], eye[2] - center[2]]);
        const x = this.normalize(this.cross(up, z));
        const y = this.cross(z, x);
        return new Float32Array([
            x[0],
            y[0],
            z[0],
            0,
            x[1],
            y[1],
            z[1],
            0,
            x[2],
            y[2],
            z[2],
            0,
            -this.dot(x, eye),
            -this.dot(y, eye),
            -this.dot(z, eye),
            1
        ]);
    }

    mat4Multiply(a, b) {
        const out = new Float32Array(16);
        for (let i = 0; i < 4; i++) {
            for (let j = 0; j < 4; j++) {
                out[j * 4 + i] =
                    a[i] * b[j * 4] + a[i + 4] * b[j * 4 + 1] + a[i + 8] * b[j * 4 + 2] + a[i + 12] * b[j * 4 + 3];
            }
        }
        return out;
    }

    /**
     * WebGL-Ressourcen aufräumen (Memory Leak Prevention)
     * Sollte aufgerufen werden wenn der Renderer nicht mehr benötigt wird
     */
    dispose() {
        const gl = this.gl;
        if (!gl) return;

        // Picking Framebuffer und zugehörige Ressourcen
        if (this.pickingFramebuffer) {
            gl.deleteFramebuffer(this.pickingFramebuffer);
            this.pickingFramebuffer = null;
        }
        if (this.pickingTexture) {
            gl.deleteTexture(this.pickingTexture);
            this.pickingTexture = null;
        }
        if (this.pickingRenderbuffer) {
            gl.deleteRenderbuffer(this.pickingRenderbuffer);
            this.pickingRenderbuffer = null;
        }

        // Shader Programs
        if (this.programs) {
            for (const key of Object.keys(this.programs)) {
                if (this.programs[key]) {
                    gl.deleteProgram(this.programs[key]);
                }
            }
            this.programs = {};
        }

        // Buffers
        if (this.buffers) {
            for (const key of Object.keys(this.buffers)) {
                if (this.buffers[key]) {
                    gl.deleteBuffer(this.buffers[key]);
                }
            }
            this.buffers = {};
        }

        // Event Listener entfernen
        window.removeEventListener('resize', this._resizeListener);
        if (this._trackedListeners) {
            for (const [target, type, fn, opts] of this._trackedListeners) {
                target.removeEventListener(type, fn, opts);
            }
            this._trackedListeners = [];
        }

        // Referenzen aufräumen
        this.game = null;
        this.explodingFields.clear();
        this.playerCameraStates.clear();

        console.info('[GameRenderer] Disposed successfully');
    }

    /**
     * Calculates distance between two touch points (for pinch-to-zoom)
     * @param {TouchList} touches
     * @returns {number}
     */
    _getTouchDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }
}

export default GameRenderer;
